// MARK://dhk8/pos/features/TicketPreview
// Thermal ticket preview (58mm / 80mm) — three variants:
//   'receipt'   — customer receipt (default): items + totals + payments + change.
//   'precuenta' — pre-bill: items + totals, NO payments, "no es comprobante" notice.
//   'kitchen'   — kitchen comanda: items only (big, no prices), modifiers + notes.
// Port of sc-restaurant TicketPreview.tsx, extended with the variant axis (Sprint D).

import type { OrderDto, RegisteredPayment } from '../stores/order-store.js'
import { formatCents } from '../lib/money.js'

export type TicketVariant = 'receipt' | 'precuenta' | 'kitchen'

export interface TicketPreviewProps {
  order: OrderDto
  variant?: TicketVariant
  payments?: RegisteredPayment[]
  tipCents?: number
  cashGivenCents?: number
  waiterName?: string | null
  tableCode?: string | null
  hasInvoice?: boolean
  /** Optional business header lines (from the print template). */
  headerLines?: string[]
  /** Optional footer lines (from the print template). */
  footerLines?: string[]
  paperWidthMm?: 58 | 80
}

const HUMAN_METHOD: Record<string, string> = {
  CASH: 'Efectivo', CARD_DEBIT: 'Tarjeta débito', CARD_CREDIT: 'Tarjeta crédito',
  TRANSFER: 'Transferencia', MEAL_VOUCHER: 'Vale',
}

export default function TicketPreview({
  order,
  variant = 'receipt',
  payments = [],
  tipCents,
  cashGivenCents,
  waiterName,
  tableCode,
  hasInvoice = false,
  headerLines,
  footerLines,
  paperWidthMm = 80,
}: TicketPreviewProps) {
  const isKitchen = variant === 'kitchen'
  const isPrecuenta = variant === 'precuenta'
  const tip = tipCents ?? order.tipCents
  const paidSum = payments.reduce((s, p) => s + p.amountCents, 0)
  const cashPaid = payments.filter((p) => p.method === 'CASH').reduce((s, p) => s + p.amountCents, 0)
  const changeCents = cashGivenCents ? Math.max(0, cashGivenCents - cashPaid) : Math.max(0, paidSum - order.totalCents)
  const dateStr = formatDateMx(order.closedAt ?? order.openedAt ?? new Date().toISOString())

  const title = isKitchen ? 'COMANDA · COCINA' : isPrecuenta ? 'PRECUENTA' : 'POS'

  return (
    <div className={`dhk8-ticket dhk8-ticket--${variant}`} data-testid="ticket-preview" data-variant={variant} style={{ width: `${paperWidthMm}mm` }}>
      <header className="dhk8-ticket__header">
        <div className="dhk8-ticket__trade">{title}</div>
        {!isKitchen && (headerLines && headerLines.length > 0
          ? headerLines.map((l, i) => <div key={i} className="dhk8-ticket__meta-line">{l}</div>)
          : <div className="dhk8-ticket__meta-line">{typeof window !== 'undefined' ? window.location.hostname : ''}</div>)}
      </header>

      <hr className="dhk8-ticket__rule" />

      <section className="dhk8-ticket__meta">
        <div className="dhk8-ticket__kv"><span>Folio</span><span>{order.folio}</span></div>
        <div className="dhk8-ticket__kv"><span>Fecha</span><span>{dateStr}</span></div>
        {tableCode && <div className="dhk8-ticket__kv"><span>Mesa</span><span>{tableCode}</span></div>}
        <div className="dhk8-ticket__kv"><span>Comensales</span><span>{order.covers}</span></div>
        {waiterName && <div className="dhk8-ticket__kv"><span>Mesero</span><span>{waiterName}</span></div>}
      </section>

      <hr className="dhk8-ticket__rule" />

      <section className="dhk8-ticket__items">
        {order.items.map((item) => (
          <div key={item.id} className="dhk8-ticket__item">
            <div className="dhk8-ticket__item-row">
              <span className="dhk8-ticket__item-qty">{item.quantity}×</span>
              <span className="dhk8-ticket__item-name">{item.name}</span>
              {!isKitchen && <span className="dhk8-ticket__item-total">{formatCents(item.totalCents)}</span>}
            </div>
            {item.modifiers.map((m, i) => (
              <div key={i} className="dhk8-ticket__item-row dhk8-ticket__item-row--sub">
                <span className="dhk8-ticket__item-qty" />
                <span className="dhk8-ticket__item-name">+ {m.name}</span>
                {!isKitchen && <span className="dhk8-ticket__item-total">{m.priceDeltaCents > 0 ? formatCents(m.priceDeltaCents) : ''}</span>}
              </div>
            ))}
            {item.notes && <div className="dhk8-ticket__item-notes">"{item.notes}"</div>}
          </div>
        ))}
      </section>

      {!isKitchen && (
        <>
          <hr className="dhk8-ticket__rule" />
          <section className="dhk8-ticket__totals">
            <div className="dhk8-ticket__kv"><span>Subtotal</span><span>{formatCents(order.subtotalCents)}</span></div>
            {order.discountCents > 0 && <div className="dhk8-ticket__kv"><span>Descuento</span><span>−{formatCents(order.discountCents)}</span></div>}
            <div className="dhk8-ticket__kv"><span>IVA 16%</span><span>{formatCents(order.taxCents)}</span></div>
            {tip > 0 && <div className="dhk8-ticket__kv"><span>Propina</span><span>{formatCents(tip)}</span></div>}
            <div className="dhk8-ticket__kv dhk8-ticket__kv--total"><span>TOTAL</span><span>{formatCents(order.totalCents)}</span></div>
          </section>
        </>
      )}

      {variant === 'receipt' && payments.length > 0 && (
        <>
          <hr className="dhk8-ticket__rule" />
          <section className="dhk8-ticket__payments">
            <div className="dhk8-ticket__section-title">Pagos</div>
            {payments.map((p) => (
              <div key={p.id} className="dhk8-ticket__kv">
                <span>{HUMAN_METHOD[p.method] ?? p.method}{p.cardLastFour ? ` ****${p.cardLastFour}` : ''}</span>
                <span>{formatCents(p.amountCents)}</span>
              </div>
            ))}
            {cashGivenCents !== undefined && cashGivenCents > 0 && (
              <div className="dhk8-ticket__kv"><span>Efectivo recibido</span><span>{formatCents(cashGivenCents)}</span></div>
            )}
            {changeCents > 0 && (
              <div className="dhk8-ticket__kv dhk8-ticket__kv--total"><span>Cambio</span><span>{formatCents(changeCents)}</span></div>
            )}
          </section>
        </>
      )}

      <hr className="dhk8-ticket__rule" />

      <footer className="dhk8-ticket__footer">
        {isKitchen ? (
          <div className="dhk8-ticket__notice">{new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</div>
        ) : isPrecuenta ? (
          <div className="dhk8-ticket__notice">Esto NO es un comprobante de pago. Solicite su ticket al pagar.</div>
        ) : footerLines && footerLines.length > 0 ? (
          footerLines.map((l, i) => <div key={i}>{l}</div>)
        ) : (
          <>
            <div>¡Gracias por su preferencia!</div>
            {!hasInvoice && <div className="dhk8-ticket__notice">Este documento no es un comprobante fiscal.</div>}
          </>
        )}
      </footer>

      <style>{ticketStyles}</style>
    </div>
  )
}

function formatDateMx(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d)
}

const ticketStyles = `
  .dhk8-ticket { background:#fff; color:#000; font-family:'IBM Plex Mono',ui-monospace,monospace; font-size:11px; line-height:1.4; padding:8mm 6mm; margin:0 auto; box-sizing:border-box; }
  .dhk8-ticket--kitchen { font-size:14px; line-height:1.5; }
  .dhk8-ticket__header { text-align:center; }
  .dhk8-ticket__trade { font-size:14px; letter-spacing:.04em; text-transform:uppercase; }
  .dhk8-ticket--kitchen .dhk8-ticket__trade { font-size:18px; font-weight:600; }
  .dhk8-ticket__meta-line { font-size:inherit; line-height:1.3; }
  .dhk8-ticket__rule { border:none; border-top:1px dashed #000; margin:6px 0; }
  .dhk8-ticket__meta,.dhk8-ticket__totals,.dhk8-ticket__payments { display:flex; flex-direction:column; gap:2px; }
  .dhk8-ticket__kv { display:flex; justify-content:space-between; gap:8px; font-variant-numeric:tabular-nums; }
  .dhk8-ticket__kv--total { font-size:13px; padding-top:2px; border-top:1px solid #000; margin-top:2px; }
  .dhk8-ticket__items { display:flex; flex-direction:column; gap:4px; }
  .dhk8-ticket--kitchen .dhk8-ticket__items { gap:8px; }
  .dhk8-ticket__item { display:flex; flex-direction:column; gap:2px; }
  .dhk8-ticket__item-row { display:grid; grid-template-columns:28px 1fr auto; gap:4px; font-variant-numeric:tabular-nums; }
  .dhk8-ticket--kitchen .dhk8-ticket__item-row { grid-template-columns:36px 1fr; }
  .dhk8-ticket__item-row--sub { color:#333; font-size:10px; }
  .dhk8-ticket--kitchen .dhk8-ticket__item-row--sub { font-size:12px; color:#000; }
  .dhk8-ticket__item-qty { text-align:right; }
  .dhk8-ticket__item-notes { padding-left:32px; font-style:italic; font-size:10px; color:#333; }
  .dhk8-ticket--kitchen .dhk8-ticket__item-notes { padding-left:40px; font-size:13px; color:#000; }
  .dhk8-ticket__section-title { font-size:10px; letter-spacing:.1em; text-transform:uppercase; }
  .dhk8-ticket__footer { text-align:center; margin-top:6px; display:flex; flex-direction:column; gap:4px; }
  .dhk8-ticket__notice { font-size:9px; color:#333; }
  .dhk8-ticket--precuenta .dhk8-ticket__notice { font-size:11px; }
  @media print {
    @page { size:auto; margin:0; }
    body.dhk8-printing > *:not(.dhk8-ticket-print-root) { display:none !important; }
    body.dhk8-printing .dhk8-ticket-print-root { position:static !important; margin:0; padding:0; }
  }
`
